<script>
import ChatMessage from 'widget/components/ChatMessage.vue';
import AgentTypingBubble from 'widget/components/AgentTypingBubble.vue';
import DateSeparator from 'shared/components/DateSeparator.vue';
import Spinner from 'shared/components/Spinner.vue';
import { useDarkMode } from 'widget/composables/useDarkMode';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'ConversationWrap',
  components: {
    ChatMessage,
    AgentTypingBubble,
    DateSeparator,
    Spinner,
  },
  props: {
    groupedMessages: {
      type: Array,
      default: () => [],
    },
  },
  setup() {
    const { darkMode } = useDarkMode();
    return { darkMode };
  },
  data() {
    return {
      previousScrollHeight: 0,
      previousConversationSize: 0,
      hasSentWelcomeMessage: false,
      isChatOpen: false,
    };
  },
  computed: {
    ...mapGetters({
      earliestMessage: 'conversation/getEarliestMessage',
      lastMessage: 'conversation/getLastMessage',
      allMessagesLoaded: 'conversation/getAllMessagesLoaded',
      isFetchingList: 'conversation/getIsFetchingList',
      conversationSize: 'conversation/getConversationSize',
      isAgentTyping: 'conversation/getIsAgentTyping',
      conversationAttributes: 'conversationAttributes/getConversationParams',
    }),
    colorSchemeClass() {
      return `${this.darkMode === 'dark' ? 'dark-scheme' : 'light-scheme'}`;
    },
    showStatusIndicator() {
      const { status } = this.conversationAttributes;
      const isConversationInPendingStatus = status === 'pending';
      const isLastMessageIncoming =
        this.lastMessage.message_type === MESSAGE_TYPE.INCOMING;
      return (
        this.isAgentTyping ||
        (isConversationInPendingStatus && isLastMessageIncoming)
      );
    },
  },
  watch: {
    allMessagesLoaded() {
      this.previousScrollHeight = 0;
    },
  },
  mounted() {
    this.$el.addEventListener('scroll', this.handleScroll);
    this.scrollToBottom();

    // 监听聊天窗口打开事件
    window.addEventListener('message', this.handleWindowMessage);

    // 如果聊天窗口已经打开，立即设置状态
    if (window.$chatwoot && window.$chatwoot.isOpen) {
      this.isChatOpen = true;
      // 延迟检查是否需要发送模拟消息
      setTimeout(() => {
        this.checkAndSendMockMessage();
      }, 1000);
    }
  },
  updated() {
    if (this.previousConversationSize !== this.conversationSize) {
      this.previousConversationSize = this.conversationSize;
      this.scrollToBottom();
    }
  },
  unmounted() {
    this.$el.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('message', this.handleWindowMessage);
    if (this.chatOpenObserver) {
      this.chatOpenObserver.disconnect();
    }
  },
  methods: {
    ...mapActions('conversation', ['fetchOldConversations']),
    scrollToBottom() {
      const container = this.$el;
      container.scrollTop = container.scrollHeight - this.previousScrollHeight;
      this.previousScrollHeight = 0;
    },
    handleScroll() {
      if (
        this.isFetchingList ||
        this.allMessagesLoaded ||
        !this.conversationSize
      ) {
        return;
      }

      if (this.$el.scrollTop < 100) {
        this.fetchOldConversations({ before: this.earliestMessage.id });
        this.previousScrollHeight = this.$el.scrollHeight;
      }
    },
    // 处理窗口消息事件
    handleWindowMessage(event) {
      // 检查是否是来自 Chatwoot 的消息
      if (event.data && typeof event.data === 'string' && event.data.startsWith('chatwoot-widget:')) {
        try {
          const parsedData = JSON.parse(event.data.replace('chatwoot-widget:', ''));

          // 检查是否是切换聊天窗口的事件
          if (parsedData.event === 'toggle-open') {
            const isOpen = parsedData.isOpen;
            console.log('聊天窗口状态变更:', isOpen ? '打开' : '关闭');

            if (isOpen) {
              this.isChatOpen = true;
              // 聊天框打开时，检查是否需要发送模拟消息
              // 添加延迟确保消息加载完成
              setTimeout(() => {
                console.log('检查是否需要发送模拟消息，当前消息数:', this.groupedMessages.length);
                this.checkAndSendMockMessage();
              }, 1000);
            } else {
              this.isChatOpen = false;
            }
          }
        } catch (error) {
          console.error('解析 Chatwoot 消息出错:', error);
        }
      }
    },
    getWelcomeMessage() {
      if (window.chatwootWebChannel) {
        // 如果后台设置了欢迎标语但是没有正确传递，则手动设置一个
        if (!window.chatwootWebChannel.welcomeTagline) {
          console.log('手动设置 welcomeTagline 属性');
          window.chatwootWebChannel.welcomeTagline = '您好，有什么可以帮助您的吗？';
        }

        return window.chatwootWebChannel.welcomeTagline;
      }

      return '👋 您好，有什么可以帮助您的吗？';
    },

    checkAndSendMockMessage() {
      console.log('checkAndSendMockMessage ignored');
      return;
      console.log('checkAndSendMockMessage 开始执行');
      console.log('当前消息列表长度:', this.groupedMessages.length);
      console.log('是否已发送欢迎消息:', this.hasSentWelcomeMessage);
      console.log('聊天框是否打开:', this.isChatOpen);

      // 检查是否已经存在欢迎消息
      const hasWelcomeMessage = Object.values(this.$store.getters['conversation/getConversation']).some(message => {
        return message.message_type === MESSAGE_TYPE.OUTGOING && message.sender && message.sender.name === '客服';
      });

      console.log('是否已存在欢迎消息:', hasWelcomeMessage);

      // 如果已经存在欢迎消息，则标记为已发送
      if (hasWelcomeMessage) {
        this.hasSentWelcomeMessage = true;
        return;
      }

      // 判断消息列表是否为空，且是否已经发送过模拟消息，以及聊天框是否已打开
      if (this.groupedMessages.length === 0 && !this.hasSentWelcomeMessage && this.isChatOpen) {
        console.log('满足发送欢迎消息的条件，准备发送');
        this.hasSentWelcomeMessage = true;

        // 获取欢迎消息
        const greetingMessage = this.getWelcomeMessage();
        console.log('获取到的欢迎消息:', greetingMessage);

        // 创建模拟的代理消息 - 使用固定的 ID 而不是随机生成
        const mockAgentMessage = {
          id: 999999999, // 使用一个固定的大数字作为 ID，避免重复生成
          content: greetingMessage,
          created_at: Math.floor(Date.now() / 1000) - 1, // 减去 1 秒，确保它是最早的消息
          message_type: MESSAGE_TYPE.OUTGOING, // 代理消息类型
          content_type: 'text',
          content_attributes: {
            welcome_message: true // 标记为欢迎消息
          },
          conversation_id: 1, // 添加会话ID
          private: false,
          sender: {
            id: 1,
            name: '',
            avatar_url: '/assets/images/chatwoot_bot.png',
          },
          status: 'sent',
          showAvatar: true,
        };
        console.log('checkAndSendMockMessage:mockAgentMessage', mockAgentMessage);

        // 将模拟消息添加到对话中
        this.$store.commit('conversation/pushMessageToConversation', mockAgentMessage);
        console.log('已发送模拟代理消息:', mockAgentMessage);
      } else {
        console.log('不需要发送模拟消息，原因：', {
          '消息列表为空': this.groupedMessages.length === 0,
          '已发送过欢迎消息': this.hasSentWelcomeMessage,
          '聊天框已打开': this.isChatOpen
        });
      }
    },
  },
};
</script>

<template>
  <div class="conversation--container" :class="colorSchemeClass">
    <div class="conversation-wrap" :class="{ 'is-typing': isAgentTyping }">
      <div v-if="isFetchingList" class="message--loader">
        <Spinner />
      </div>
      <div
        v-for="groupedMessage in groupedMessages"
        :key="groupedMessage.date"
        class="messages-wrap"
      >
        <DateSeparator :date="groupedMessage.date" />
        <ChatMessage
          v-for="message in groupedMessage.messages"
          :key="message.id"
          :message="message"
        />
      </div>
      <AgentTypingBubble v-if="showStatusIndicator" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.conversation--container {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  color-scheme: light dark;

  &.light-scheme {
    color-scheme: light;
  }

  &.dark-scheme {
    color-scheme: dark;
  }
}

.conversation-wrap {
  flex: 1;
  @apply px-2 pt-8 pb-2;
}

.message--loader {
  text-align: center;
}
</style>
